import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { format } from 'date-fns';
import { EnvironmentService } from '../../environments/environment-loader';
import {
  AnalyticsFilters,
  AvailableJobsResponse,
  AvailableCandidatesResponse,
  ApplicationsResponse,
  CreatedJobsResponse,
  RegisteredCandidatesResponse,
  HiringRateResponse,
  SupplyDemandResponse,
  MarketSummaryResponse,
  ApiResponse
} from '../models/analytics.models';

/**
 * Analytics Service
 * Handles all API calls for Labor Market Analytics
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly apiUrl: string;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {
    this.apiUrl = `${this.environmentService.environment.openApiUrl}api/analytics/labor-market`;
  }

  /**
   * Build HTTP headers with x-client-type
   */
  private buildHeaders(): HttpHeaders {
    return new HttpHeaders().set('x-client-type', 'candidate');
  }

  /**
   * Build HTTP query parameters from filters
   * Formats dates to ISO 8601 format: yyyy-MM-dd'T'HH:mm:ss'Z'
   */
  private buildParams(filters: AnalyticsFilters): HttpParams {
    let params = new HttpParams()
      .set('dateFrom', format(filters.dateFrom, "yyyy-MM-dd'T'HH:mm:ss'Z'"))
      .set('dateTo', format(filters.dateTo, "yyyy-MM-dd'T'HH:mm:ss'Z'"))
      .set('period', filters.period);

    if (filters.categoryId !== undefined && filters.categoryId !== null) {
      params = params.set('categoryId', filters.categoryId.toString());
    }
    if (filters.positionId !== undefined && filters.positionId !== null) {
      params = params.set('positionId', filters.positionId.toString());
    }
    if (filters.city) {
      params = params.set('city', filters.city);
    }
    if (filters.country) {
      params = params.set('country', filters.country);
    }

    return params;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred while fetching analytics data';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    console.error('Analytics API Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Get available jobs analytics
   * Endpoint: GET /api/analytics/labor-market/jobs/available
   */
  getAvailableJobs(filters: AnalyticsFilters): Observable<AvailableJobsResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<AvailableJobsResponse>>(`${this.apiUrl}/jobs/available`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get available candidates analytics
   * Endpoint: GET /api/analytics/labor-market/candidates/available
   */
  getAvailableCandidates(filters: AnalyticsFilters): Observable<AvailableCandidatesResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<AvailableCandidatesResponse>>(`${this.apiUrl}/candidates/available`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get applications analytics
   * Endpoint: GET /api/analytics/labor-market/applications
   */
  getApplications(filters: AnalyticsFilters): Observable<ApplicationsResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<ApplicationsResponse>>(`${this.apiUrl}/applications`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get created jobs analytics
   * Endpoint: GET /api/analytics/labor-market/jobs/created
   */
  getCreatedJobs(filters: AnalyticsFilters): Observable<CreatedJobsResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<CreatedJobsResponse>>(`${this.apiUrl}/jobs/created`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get registered candidates analytics
   * Endpoint: GET /api/analytics/labor-market/candidates/registered
   */
  getRegisteredCandidates(filters: AnalyticsFilters): Observable<RegisteredCandidatesResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<RegisteredCandidatesResponse>>(`${this.apiUrl}/candidates/registered`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get hiring rate analytics
   * Endpoint: GET /api/analytics/labor-market/hiring-rate
   */
  getHiringRate(filters: AnalyticsFilters): Observable<HiringRateResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<HiringRateResponse>>(`${this.apiUrl}/hiring-rate`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get supply vs demand analytics
   * Endpoint: GET /api/analytics/labor-market/supply-demand
   */
  getSupplyDemand(filters: AnalyticsFilters): Observable<SupplyDemandResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<SupplyDemandResponse>>(`${this.apiUrl}/supply-demand`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }

  /**
   * Get market summary (dashboard endpoint)
   * Endpoint: GET /api/analytics/labor-market/summary
   */
  getMarketSummary(filters: AnalyticsFilters): Observable<MarketSummaryResponse> {
    const params = this.buildParams(filters);
    const headers = this.buildHeaders();
    return this.http
      .get<ApiResponse<MarketSummaryResponse>>(`${this.apiUrl}/summary`, { params, headers })
      .pipe(
        map(response => response.body),
        catchError(this.handleError)
      );
  }
}
